/**
 * The "Send update" pop-up: find a client, choose which of their open cases it covers, draft one
 * update, and send it by email, text and portal. It is recorded once on each chosen case.
 */

export interface ClientUpdateRecipient {
  User_guid: string;
  name: string;
  email: string | null;
  phone: string | null;
}

export interface ClientUpdateCaseNote {
  date: string;
  author: string | null;
  text: string;
}

export interface ClientUpdateCaseDocument {
  guid: string;
  name: string;
  date: string;
}

export interface ClientUpdateCase {
  ticketGuid: string;
  citationNumber: string | null;
  courtName: string | null;
  statusName: string | null;
  latestNote?: ClientUpdateCaseNote | null;
  latestDocument?: ClientUpdateCaseDocument | null;
}

export interface ClientUpdateCases {
  cases: ClientUpdateCase[];
  recipientEmail: string | null;
  phone: string | null;
}

export interface ClientUpdateDraft {
  subject: string;
  body: string;
  recipientEmail: string | null;
  /** False when the AI was unavailable and the short portal note was offered instead. */
  aiDrafted: boolean;
}

export interface ClientUpdateDelivery {
  email: boolean;
  text: { sent: boolean; refused?: 'no-phone' | 'no-consent' | string };
  portal: true;
  citations: string[];
  recipientEmail: string | null;
}

/**
 * The CDL Consultants or CDL Legal permission request for one case (Lloyd, 14 and 21 Oct 2026;
 * CDL Legal, Hannah, 30 Oct 2026).
 */
export interface CdlConsentRequestStatus {
  /** CDL Consultants or CDL Legal referred this case, so the question exists here at all. */
  applies: boolean;
  /** Which of the two referred it. Present when `applies`. */
  partner?: 'CDL_CONSULTANTS' | 'CDL_LEGAL';
  /** Its name as the client reads it: "CDL Consultants" or "CDL Legal". */
  partnerName?: string;
  /** Why it cannot be sent, or null when it can. */
  cannotSend: string | null;
  /** Which of text and email it goes by (Lloyd, 21 Oct 2026); the portal copy always goes too. */
  channels?: { text: boolean; email: boolean };
  /** Why it will not go by text, when it will not. */
  noTextReason?: string | null;
  /** When the waiting request went, or null when none is waiting. */
  openRequestSentAt: string | null;
  /** Lloyd's exact text, so staff read what the client will get. */
  text: string;
}

async function readError(response: Response, fallback: string): Promise<string> {
  try {
    const data = await response.json();
    return data?.message || fallback;
  } catch {
    return fallback;
  }
}

class ClientUpdateApi {
  private static base(): string {
    const host = process.env.API_HOST?.trim() || window.location.origin;
    return `${host}/api/v1/manager/client-update`;
  }

  /** Clients matching a name, email or phone number. */
  static async search(term: string): Promise<ClientUpdateRecipient[]> {
    const query = new URLSearchParams({ q: term });
    const response = await fetch(`${ClientUpdateApi.base()}/search?${query.toString()}`, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(await readError(response, 'Failed to search clients'));
    }
    return response.json();
  }

  /** The client's open cases, which an update can cover. */
  static async cases(userGuid: string): Promise<ClientUpdateCases> {
    const response = await fetch(`${ClientUpdateApi.base()}/${userGuid}/cases`, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(await readError(response, 'Failed to load the client’s cases'));
    }
    return response.json();
  }

  /** A draft covering the chosen cases, for staff to read and edit. Nothing is sent. */
  static async draft(userGuid: string, ticketGuids: string[]): Promise<ClientUpdateDraft> {
    const response = await fetch(`${ClientUpdateApi.base()}/${userGuid}/draft`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ ticketGuids }),
    });
    if (!response.ok) {
      throw new Error(await readError(response, 'Failed to draft the update'));
    }
    return response.json();
  }

  /**
   * A reply to the client's latest message, drafted from what they wrote and their cases (Lloyd,
   * 14 Oct 2026). Nothing is sent; staff edit it and use `send`.
   */
  static async replyDraft(userGuid: string): Promise<ClientUpdateDraft & { clientMessage: string }> {
    const response = await fetch(`${ClientUpdateApi.base()}/${userGuid}/reply-draft`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(await readError(response, 'Failed to draft the reply'));
    }
    return response.json();
  }

  static async send(
    userGuid: string,
    subject: string,
    body: string,
    ticketGuids: string[],
  ): Promise<ClientUpdateDelivery> {
    const response = await fetch(`${ClientUpdateApi.base()}/${userGuid}/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ subject, body, ticketGuids }),
    });
    if (!response.ok) {
      throw new Error(await readError(response, 'Failed to send the update'));
    }
    return response.json();
  }

  static async cdlConsentRequest(ticketGuid: string): Promise<CdlConsentRequestStatus> {
    const response = await fetch(`${ClientUpdateApi.base()}/ticket/${ticketGuid}/cdl-consent-request`, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(await readError(response, 'Failed to read the CDL permission request'));
    }
    return response.json();
  }

  /** Texts Lloyd's message about this one case. Rejects with the server's reason when it cannot go. */
  static async sendCdlConsentRequest(ticketGuid: string): Promise<void> {
    const response = await fetch(`${ClientUpdateApi.base()}/ticket/${ticketGuid}/cdl-consent-request`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(await readError(response, 'The permission request could not be sent.'));
    }
  }
}

export default ClientUpdateApi;
